// D02.9 — mute-safe audio catalog.
//
// Text is primary; audio is an overlay. Every cue resolves its exact subtitle
// from the manifest without touching audio bytes, so muting loses no evidence:
// a muted catalog answers the same subtitles as an audible one.

import type { AudioManifest, AudioRef, FootstepSurface, MusicClip, MusicRole, MusicState, SfxClip, VoiceCategory, VoiceClip } from './manifest.ts';

export interface AudioCue {
  readonly textId: string;
  readonly subtitle: string;
  readonly category: VoiceCategory;
  readonly presentation: VoiceClip['presentation'];
  readonly speaker: VoiceClip['speaker'];
  readonly pronunciation: VoiceClip['pronunciation'];
  /** Null when the catalog is muted. The subtitle is identical either way. */
  readonly audio: AudioRef | null;
}

export interface AudioCatalog {
  readonly manifest: AudioManifest;
  readonly muted: boolean;
  clip(id: string): VoiceClip | undefined;
  byTextId(textId: string): readonly VoiceClip[];
  /** Every cue for an approved text id. Unknown ids return an empty array — never a fabricated line. */
  cuesForText(textId: string): readonly AudioCue[];
  subtitles(): ReadonlyMap<string, string>;
  ambience(zone?: string): readonly SfxClip[];
  foley(material?: string): readonly SfxClip[];
  habits(): readonly SfxClip[];
  surface(area: string): FootstepSurface;
  footstep(area: string): SfxClip | undefined;
  music(role?: MusicRole, zone?: string): readonly MusicClip[];
  musicState(state: MusicState): MusicClip | undefined;
}

export function createAudioCatalog(manifest: AudioManifest, options: { readonly muted?: boolean } = {}): AudioCatalog {
  const muted = options.muted === true;
  const byId = new Map<string, VoiceClip>();
  const byText = new Map<string, VoiceClip[]>();
  for (const clip of manifest.clips) {
    byId.set(clip.id, clip);
    const list = byText.get(clip.sourceTextId) ?? [];
    list.push(clip);
    byText.set(clip.sourceTextId, list);
  }
  const toCue = (clip: VoiceClip): AudioCue => ({
    textId: clip.sourceTextId,
    subtitle: clip.subtitle,
    category: clip.category,
    presentation: clip.presentation,
    speaker: clip.speaker,
    pronunciation: clip.pronunciation,
    audio: muted ? null : clip.audio,
  });
  return {
    manifest,
    muted,
    clip: id => byId.get(id),
    byTextId: textId => byText.get(textId) ?? [],
    cuesForText: textId => (byText.get(textId) ?? []).map(toCue),
    subtitles: () => new Map(manifest.clips.map(clip => [clip.sourceTextId, clip.subtitle])),
    ambience: zone => manifest.sfx.filter(clip => clip.kind === 'ambience' && (zone === undefined || clip.zone === zone)),
    foley: material => manifest.sfx.filter(clip => clip.kind === 'foley' && (material === undefined || clip.material === material)),
    habits: () => manifest.sfx.filter(clip => clip.kind === 'habit'),
    surface: area => manifest.surfaces[area] ?? 'stone',
    footstep: area => manifest.sfx.find(clip => clip.id === `sfx:footstep:${manifest.surfaces[area] ?? 'stone'}`),
    music: (role, zone) => Object.values(manifest.music).filter(clip =>
      (role === undefined || clip.role === role) && (zone === undefined || clip.zone === undefined || clip.zone === zone)),
    musicState: state => Object.values(manifest.music).find(clip => clip.state === state),
  };
}
